import React, { useEffect, useId, useState } from 'react';
import { ASSET_COLORS } from '../plotTheme';

// Drawing is a cross: Utility on the left, Load on the right, Solar on top, Battery below.
const W = 420;
const H = 120;
const CX = 207;
const CY = 60;
const CAP = 4; // keeps the round line caps inside the viewBox

const CYCLE = "2.4s";

const COLORS = ASSET_COLORS;

// Inbound comets travel to the junction first, outbound comets leave it afterwards
const INBOUND = { keyTimes: "0;0.55;1", hold: "end" };
const OUTBOUND = { keyTimes: "0;0.3;1", hold: "start" };

// Each arm is described in a local frame where the flow runs along +x from 0 to len
const ARMS = {
  gridIn:           { side: "grid",    transform: `translate(0 ${CY})`,                len: CX,     comet: 90, phase: INBOUND },
  gridOut:          { side: "grid",    transform: `translate(${CX} ${CY}) rotate(180)`, len: CX,     comet: 90, phase: OUTBOUND },
  solarIn:          { side: "solar",   transform: `translate(${CX} 0) rotate(90)`,      len: CY,     comet: 36, phase: INBOUND },
  batteryDischarge: { side: "battery", transform: `translate(${CX} ${H}) rotate(-90)`,  len: H - CY, comet: 36, phase: INBOUND },
  batteryCharge:    { side: "battery", transform: `translate(${CX} ${CY}) rotate(90)`,  len: H - CY, comet: 36, phase: OUTBOUND },
  loadOut:          { side: "load",    transform: `translate(${CX} ${CY})`,             len: W - CX, comet: 90, phase: OUTBOUND },
};

// Track segment from the junction to each outer end
const TRACKS = {
  grid:    { x2: CAP,     y2: CY },
  solar:   { x2: CX,      y2: CAP },
  battery: { x2: CX,      y2: H - CAP },
  load:    { x2: W - CAP, y2: CY },
};

// Same rules the backend used for animation_index: anything within ±THRESHOLD kW is idle.
// Sign conventions: grid +import/-export, battery +charging/-discharging, load +consuming.
const THRESHOLD = 0.1;

const activeArms = ({ gridPower, solarPower, batteryPower, loadPower }) => [
  gridPower > THRESHOLD && "gridIn",
  gridPower < -THRESHOLD && "gridOut",
  // Solar meters differ in sign between sites, and solar only ever generates, so use the magnitude
  Math.abs(solarPower ?? 0) > THRESHOLD && "solarIn",
  batteryPower < -THRESHOLD && "batteryDischarge",
  batteryPower > THRESHOLD && "batteryCharge",
  loadPower > THRESHOLD && "loadOut",
].filter(Boolean);

// SMIL ignores CSS media queries, so honour reduced motion by not rendering the comets at all
const usePrefersReducedMotion = () => {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => Boolean(window.matchMedia?.(query).matches));

  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql) return;
    const onChange = (e) => setReduced(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
};

const Comet = ({ id, arm }) => {
  const { side, transform, len, comet, phase } = ARMS[arm];
  const end = len + comet;
  const values = phase.hold === "end" ? `0;${end};${end}` : `0;0;${end}`;

  return (
    <g transform={transform}>
      <clipPath id={`${id}-clip-${arm}`}>
        <rect x={0} y={-8} width={len} height={16} />
      </clipPath>
      <g clipPath={`url(#${id}-clip-${arm})`}>
        {/* Head sits at x=0 and the tail tapers back; parked at 0 it is fully clipped */}
        <g filter={`url(#${id}-glow)`}>
          <path
            d={`M ${-comet} -0.4 L -2 -2.2 L -2 2.2 L ${-comet} 0.4 Z`}
            fill={`url(#${id}-tail-${side})`}
          />
          <circle r={3.2} fill={COLORS[side]} />
          <circle r={1.4} fill="#fff" opacity={0.9} />
          <animateTransform
            attributeName="transform"
            type="translate"
            values={values}
            keyTimes={phase.keyTimes}
            calcMode="spline"
            keySplines="0.45 0 0.55 1; 0.45 0 0.55 1"
            dur={CYCLE}
            repeatCount="indefinite"
          />
        </g>
      </g>
    </g>
  );
};

const EnergyFlow = ({ gridPower, solarPower, batteryPower, loadPower, ...props }) => {
  const id = useId().replace(/:/g, "");
  const reducedMotion = usePrefersReducedMotion();
  const arms = activeArms({ gridPower, solarPower, batteryPower, loadPower });
  const activeSides = new Set(arms.map((arm) => ARMS[arm].side));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Energy flow" {...props}>
      <defs>
        {Object.entries(COLORS).map(([side, color]) => (
          <linearGradient key={side} id={`${id}-tail-${side}`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor={color} stopOpacity="0" />
            <stop offset="0.7" stopColor={color} stopOpacity="0.55" />
            <stop offset="1" stopColor={color} />
          </linearGradient>
        ))}
        {/* Fixed region so the blur isn't cropped on the thin, zero-height comet bounding box */}
        <filter id={`${id}-glow`} filterUnits="userSpaceOnUse" x={-W} y={-W} width={W * 3} height={W * 3}>
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Base tracks, tinted with the source colour while that arm carries power */}
      <g strokeLinecap="round" strokeWidth={4} fill="none">
        {Object.entries(TRACKS).map(([side, { x2, y2 }]) => (
          <line key={side} x1={CX} y1={CY} x2={x2} y2={y2} style={{ stroke: "var(--c-border)" }} />
        ))}
        {Object.entries(TRACKS).map(([side, { x2, y2 }]) => activeSides.has(side) && (
          <line key={side} x1={CX} y1={CY} x2={x2} y2={y2} stroke={COLORS[side]} strokeOpacity={0.22} />
        ))}
      </g>

      {/* Outer end dots */}
      {Object.entries(TRACKS).map(([side, { x2, y2 }]) => (
        <circle
          key={side}
          cx={x2}
          cy={y2}
          r={3}
          style={{ fill: activeSides.has(side) ? COLORS[side] : "var(--c-border-strong)" }}
        />
      ))}

      {!reducedMotion && arms.map((arm) => <Comet key={arm} id={id} arm={arm} />)}

      {/* Junction node, pulsing softly while anything flows */}
      {arms.length > 0 && !reducedMotion && (
        <circle cx={CX} cy={CY} r={7} fill="none" strokeWidth={1.5} style={{ stroke: "var(--c-accent)" }}>
          <animate attributeName="r" values="7;14" dur={CYCLE} repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.5;0" dur={CYCLE} repeatCount="indefinite" />
        </circle>
      )}
      <circle
        cx={CX}
        cy={CY}
        r={7}
        strokeWidth={2.5}
        style={{
          fill: "var(--c-surface)",
          stroke: arms.length > 0 ? "var(--c-accent)" : "var(--c-border-strong)",
        }}
      />
    </svg>
  );
};

export default EnergyFlow;
